import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { X, MapPin, Info, UserPlus } from 'lucide-react';
import { KeyList } from '@/components/keys/KeyList';
import { GenerateKeyButton } from '@/components/keys/GenerateKeyButton';
import { InfoDialog, SHARE_KEY_STEPS } from '@/components/info/InfoDialog';
import { Button } from '@/components/ui/button';
import type { AccessKey, Device, DeviceAssignment } from '@/types';

interface DevicePanelProps {
  assignment: DeviceAssignment | null;
  keys: AccessKey[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRevoke: (keyId: string) => void;
  revokingId: string | null;
}

/**
 * DevicePanel — slide-up glass sheet showing one device's keys.
 *
 * Opened from a DeviceTile. Contains the key carousel (QR, wallet, share,
 * revoke via the existing KeyCard), the generate button for single-use
 * devices without an active key (anläggningsdörrar auto-hämtar i stället —
 * ingen manuell nyckelskapning), a "Tilldela nyckel"-action (SMS-flödet på
 * /send-key, förvald enhet) and an info-ikon som förklarar hur man delar.
 */
export function DevicePanel({
  assignment,
  keys,
  open,
  onOpenChange,
  onRevoke,
  revokingId,
}: DevicePanelProps) {
  const navigate = useNavigate();
  const device = assignment?.device;
  if (!device) return null;

  const deviceMap: Record<string, Device> = { [device.id]: device };
  // Anläggningsdörrar skapar inga nycklar manuellt i appen: nycklarna kommer
  // med accepterad anläggningsinbjudan, och saknas en aktiv nyckel hämtas den
  // automatiskt när panelen öppnas (GenerateKeyButton i auto-läge).
  const isFacilityDoor = assignment!.source === 'facility';
  const autoFetchKey =
    isFacilityDoor && assignment!.can_generate_keys && keys.length === 0;
  const canGenerate =
    !isFacilityDoor &&
    assignment!.can_generate_keys &&
    assignment!.key_type === 'single_use' &&
    keys.length === 0;
  const canAssign = assignment!.can_generate_keys && !!device.device_code;

  const handleAssign = () => {
    onOpenChange(false);
    navigate(`/send-key?device=${device.id}`);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background/60 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-lg max-h-[90dvh] overflow-y-auto rounded-t-3xl glass-thick px-4 pt-3 pb-[calc(1.5rem+env(safe-area-inset-bottom))] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom duration-300"
          aria-describedby={undefined}
        >
          {/* Drag handle */}
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[hsl(var(--foreground)/0.2)]" />

          {/* Header */}
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-lg font-semibold tracking-tight truncate">
                {device.name}
              </DialogPrimitive.Title>
              {device.location && (
                <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3 shrink-0" />
                  {device.location}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <InfoDialog title="Så delar du en nyckel" steps={SHARE_KEY_STEPS}>
                <button
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--foreground)/0.06)] ring-1 ring-[hsl(var(--glass-stroke))] transition-all hover:scale-105 active:scale-95"
                  aria-label="Så delar du en nyckel"
                >
                  <Info className="h-4 w-4 text-foreground/85" />
                </button>
              </InfoDialog>
              <DialogPrimitive.Close
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--foreground)/0.06)] ring-1 ring-[hsl(var(--glass-stroke))] transition-all hover:scale-105 active:scale-95"
                aria-label="Stäng"
              >
                <X className="h-4 w-4 text-foreground/85" />
              </DialogPrimitive.Close>
            </div>
          </div>

          {(canGenerate || autoFetchKey) && (
            <div className="mb-4">
              <GenerateKeyButton
                deviceId={device.id}
                deviceName={device.name}
                auto={autoFetchKey}
              />
            </div>
          )}

          <KeyList
            keys={keys}
            isLoading={false}
            canShareKeys={assignment!.can_share_keys}
            onRevoke={onRevoke}
            revokingId={revokingId}
            deviceMap={deviceMap}
          />

          {canAssign && (
            <Button size="lg" className="mt-4 w-full" onClick={handleAssign}>
              <UserPlus className="h-5 w-5" />
              Tilldela nyckel
            </Button>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
